import { v4 as uuid } from 'uuid';

import {
    defaultRetryPolicy,
    fetchWithRetry,
    RETRY_WINDOW_MS,
    RetryPolicy,
} from '~/backend/network-retry';
import { startDeadline } from '~/backend/requestDeadline';
import {
    ApprovedLockError,
    MissingPreconditionError,
    PersistenceRequestError,
    StaleVersionError,
} from '~/domain/persistence/PersistenceErrors';

/**
 * Shared HTTP plumbing for the hand-written dataset save clients
 * (structure sets, registrations, image/dose deletes).
 *
 * The generated client cannot make these calls: it has no multipart body
 * support, cannot send the `If-Match` header, and discards both the ETag
 * response header and the 409 body's `currentVersion` extension — all of
 * which the save contract depends on. Conventions mirror
 * `connector-next-fetcher.ts`: same-origin relative base URL, and only the
 * ProblemDetails `title` is ever read from an error body (`detail` may
 * carry PHI and is deliberately never parsed).
 */

const BASE_URL = '';

/**
 * Total time one save attempt may spend waiting between attempts of its
 * requests. A save fans out into several sequential requests, and during
 * a long outage each would otherwise use its full retry window in turn;
 * once this is spent, later requests get a single attempt, so the save
 * settles into per-entry failures the user can retry instead of holding
 * the session's one in-flight save for minutes. Only the backoff waits
 * count: a slow but successful attempt is not an outage.
 */
export const SAVE_RETRY_BUDGET_MS = 60_000;

/**
 * How long one attempt of a save request may wait for its response
 * headers, before the allowance for an upload's size. For a small body
 * this is well inside {@link REQUEST_TIMEOUT_MS}, so an attempt stuck on
 * a socket that died during a network change is retried within the
 * request's deadline rather than consuming it. The connector answers a
 * save request from its database, so a longer silence is not progress.
 *
 * A large upload is the exception: its attempt is allowed the time its
 * size takes at the minimum upload rate, which beyond a few megabytes
 * exceeds the deadline, and `fetch` reports no upload progress by which
 * a stalled upload could be told from a slow one. A silent stall then
 * uses the whole deadline.
 */
const ATTEMPT_RESPONSE_TIMEOUT_MS = 20_000;

const SAVE_ATTEMPT_HEADER = 'X-Save-Attempt-Id';

/** State shared by every request of one save attempt. */
type SaveAttemptContext = {
    /**
     * Client-minted id tagged onto each request, so the 1–10 requests of
     * one user "Save" read as ONE traceable clinical event in the
     * connector log (its per-request correlation ids are unrelated to
     * each other). Unchanged across transport retries of a request.
     */
    readonly id: string;
    /** Retry time left for the rest of the attempt, in milliseconds. */
    retryBudgetMs: number;
    /** Requests of this attempt currently between retries. */
    retryingRequests: number;
    /** Told whether any request of this attempt is retrying. */
    readonly onRetrying: ((retrying: boolean) => void) | null;
};

/**
 * The save attempt currently in flight, if any. Module-scoped rather
 * than threaded through the savers: saves are exclusive
 * (PatientSaveService.inFlight), and a request that is not part of a save
 * goes through {@link sendOutsideSaveAttempt}, which never joins it.
 */
let currentSaveAttempt: SaveAttemptContext | null = null;

/**
 * Runs one save attempt: every request {@link send} issues while `work`
 * is pending carries a freshly minted attempt id and draws on one
 * shared retry budget ({@link SAVE_RETRY_BUDGET_MS}). Cleared in finally
 * so an aborted save cannot leak its id onto later traffic.
 *
 * @param work - The save.
 * @param onRetrying - Told `true` when a request of this attempt starts
 *     retrying after a transient failure, and `false` once no request
 *     is retrying any more.
 */
export async function runSaveAttempt<T>(
    work: () => Promise<T>,
    onRetrying?: (retrying: boolean) => void
): Promise<T> {
    currentSaveAttempt = {
        id: uuid(),
        retryBudgetMs: SAVE_RETRY_BUDGET_MS,
        retryingRequests: 0,
        onRetrying: onRetrying ?? null,
    };
    try {
        return await work();
    } finally {
        currentSaveAttempt = null;
    }
}

/** Error-body fields the save clients are allowed to read. */
type ProblemShape = {
    title: string | null;
    /** ConcurrencyGuard's 409 extension carrying the row's live token. */
    currentVersion: string | null;
    /**
     * Whether the response was declared as ProblemDetails. Read from the
     * content type, NOT from whether a title parsed: the 409 stale-version
     * token must keep working even against a server that mislabels the body,
     * so parsing stays unconditional and this flag is advisory on top.
     *
     * It exists for one decision — an idempotent DELETE. A 404 the connector
     * declared is "the row is gone"; a 404 from a reverse proxy in front of it
     * is "your request never arrived", and those mean opposite things.
     */
    isProblemDetails: boolean;
};

async function readProblem(response: Response): Promise<ProblemShape> {
    const isProblemDetails = (
        response.headers.get('content-type') ?? ''
    ).includes('application/problem+json');
    try {
        const body: unknown = await response.json();
        if (typeof body !== 'object' || body === null) {
            return { title: null, currentVersion: null, isProblemDetails };
        }
        const record = body as Record<string, unknown>;
        return {
            title: typeof record.title === 'string' ? record.title : null,
            currentVersion:
                typeof record.currentVersion === 'string'
                    ? record.currentVersion
                    : null,
            isProblemDetails,
        };
    } catch {
        return { title: null, currentVersion: null, isProblemDetails };
    }
}

/**
 * Maps a non-ok response to the typed persistence errors. Never returns.
 *
 * @param response - The failed response.
 * @param afterInterruptedAttempt - Whether an earlier attempt of the same
 *     request failed first (see {@link StaleVersionError}).
 */
async function throwForResponse(
    response: Response,
    afterInterruptedAttempt: boolean
): Promise<never> {
    const problem = await readProblem(response);
    if (response.status === 428) {
        throw new MissingPreconditionError();
    }
    if (response.status === 409) {
        // ConcurrencyGuard's stale-version 409 carries `currentVersion`;
        // the approved-lock 409 does not.
        if (problem.currentVersion !== null) {
            throw new StaleVersionError(
                problem.currentVersion,
                afterInterruptedAttempt
            );
        }
        throw new ApprovedLockError();
    }
    throw new PersistenceRequestError(
        response.status,
        problem.title,
        problem.isProblemDetails
    );
}

/**
 * Deadline for an ordinary save request. Generous — a busy connector on a
 * clinical network can be slow — but finite: without one, a half-open socket
 * (a dropped VPN, a proxy that blackholes) leaves the promise pending forever,
 * so `PatientSaveService.inFlight` never clears, the header spins for the rest
 * of the session, and every later save is refused with "A save is already in
 * progress". A hung request has to degrade to a per-entry failure the user can
 * retry, not a wedged session.
 */
const REQUEST_TIMEOUT_MS = 60_000;

/**
 * Deadline for a bulk upload. The derived-series upload can carry up to 512 MB
 * in one request, which on a slow link legitimately takes minutes.
 */
const UPLOAD_TIMEOUT_MS = 10 * 60_000;

/**
 * The retry choice a save request gets when its client states none:
 * {@link defaultRetryPolicy}, except that a DELETE retries too — its
 * replay meets the connector's declared 404, which the savers count as
 * gone (`deleteDatasetRow`).
 */
function defaultSaveRetryPolicy(method: string, headers: Headers): RetryPolicy {
    return method === 'DELETE'
        ? 'transient'
        : defaultRetryPolicy(method, headers);
}

/** Per-request choices a save client may add to {@link send}. */
export type SendOptions = {
    /**
     * Overrides {@link defaultSaveRetryPolicy}; a client passing
     * 'transient' on an unguarded POST documents why a duplicate delivery
     * is harmless to it.
     */
    retryPolicy?: RetryPolicy;
    /**
     * Told when the request is about to be retried, i.e. an earlier
     * attempt failed and may nevertheless have reached the server.
     */
    onRetry?: () => void;
    /**
     * The request's name in the retry log, as a method and URL template
     * (`GET /api/patients/{id}`). Set by a client whose path carries an
     * identifier that {@link pathTemplate} cannot be relied on to mask.
     */
    label?: string;
};

/**
 * The request path with every segment that carries a digit replaced by
 * `{id}`, for the retry log: dataset ids, ROI numbers and DICOM UIDs all
 * carry digits, route names never do. A client whose identifier may be
 * digit-free passes an explicit label instead.
 */
function pathTemplate(path: string): string {
    return path
        .split('/')
        .map((segment) => (/\d/.test(segment) ? '{id}' : segment))
        .join('/');
}

/**
 * Sends one save request.
 *
 * Transient failures (a network change, a temporary server error) are
 * retried inside the request's deadline and within the save attempt's
 * shared retry budget. The response body is read inside the deadline
 * too: resolving the headers is not the same as completing the request,
 * and a socket that stalls after the headers arrive would otherwise
 * leave a later `.json()` hanging forever — the exact wedge the deadline
 * exists to prevent. Every response on this path is small JSON or empty,
 * so the downstream reads are memory-only.
 *
 * @param path - Request path, same-origin.
 * @param init - Method, headers and body.
 * @param ifMatch - Version token sent as `If-Match`.
 * @param options - Retry choice and retry notification.
 */
export function send(
    path: string,
    init: RequestInit,
    ifMatch?: string,
    options: SendOptions = {}
): Promise<Response> {
    return request(path, init, ifMatch, options, currentSaveAttempt);
}

/**
 * Sends one request that is not part of a save, even while one is in
 * flight: it carries no attempt id and draws on no attempt's retry
 * budget, and is otherwise sent as {@link send} sends.
 */
export function sendOutsideSaveAttempt(
    path: string,
    init: RequestInit,
    ifMatch?: string,
    options: SendOptions = {}
): Promise<Response> {
    return request(path, init, ifMatch, options, null);
}

/** {@link send}, as part of `attempt`, or of no save when it is null. */
async function request(
    path: string,
    init: RequestInit,
    ifMatch: string | undefined,
    options: SendOptions,
    attempt: SaveAttemptContext | null
): Promise<Response> {
    const headers = new Headers(init.headers);
    if (ifMatch !== undefined) {
        headers.set('If-Match', `"${ifMatch}"`);
    }
    if (attempt !== null) {
        headers.set(SAVE_ATTEMPT_HEADER, attempt.id);
    }

    const method = (init.method ?? 'GET').toUpperCase();
    const retryWindowMs = Math.max(
        0,
        Math.min(RETRY_WINDOW_MS, attempt?.retryBudgetMs ?? RETRY_WINDOW_MS)
    );

    // Body-carrying uploads get the longer deadline.
    const timeoutMs =
        init.body instanceof FormData ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS;
    const deadline = startDeadline(timeoutMs, init.signal);
    /** Backoff waited so far by this request; undefined before any retry. */
    let retryWaitMs: number | undefined = undefined;

    try {
        const response = await fetchWithRetry(
            async () => ({
                url: `${BASE_URL}${path}`,
                init: { ...init, headers, signal: deadline.signal },
            }),
            {
                signal: deadline.signal,
                label: options.label ?? `${method} ${pathTemplate(path)}`,
                retryPolicy:
                    options.retryPolicy ??
                    defaultSaveRetryPolicy(method, headers),
                // A spent budget leaves a zero window: one attempt.
                retryWindowMs,
                responseTimeoutMs: ATTEMPT_RESPONSE_TIMEOUT_MS,
                onRetry: ({ delayMs }) => {
                    options.onRetry?.();
                    if (retryWaitMs === undefined) {
                        retryWaitMs = 0;
                        if (attempt !== null) {
                            attempt.retryingRequests += 1;
                            attempt.onRetrying?.(true);
                        }
                    }
                    retryWaitMs += delayMs;
                },
            }
        );

        if (!response.ok) {
            await throwForResponse(response, retryWaitMs !== undefined);
        }
        return response;
    } catch (error) {
        // throwForResponse's typed errors must pass through untouched; only a
        // transport failure maps to the generic request error. An abort is one
        // of those, and should be: both mean "this entry did not save, try
        // again". So is deadline expiry, which reaches here as an abort.
        if (
            error instanceof MissingPreconditionError ||
            error instanceof StaleVersionError ||
            error instanceof ApprovedLockError ||
            error instanceof PersistenceRequestError
        ) {
            throw error;
        }
        throw new PersistenceRequestError(null, null);
    } finally {
        deadline.clear();
        if (retryWaitMs !== undefined && attempt !== null) {
            attempt.retryBudgetMs -= retryWaitMs;
            attempt.retryingRequests -= 1;
            if (attempt.retryingRequests === 0) {
                attempt.onRetrying?.(false);
            }
        }
    }
}

/** Parses a JSON body, mapping a malformed one to a request error. */
export async function parseJsonBody<T>(response: Response): Promise<T> {
    try {
        return (await response.json()) as T;
    } catch {
        throw new PersistenceRequestError(response.status, null);
    }
}

/**
 * Reads a post-update version from the ETag response header. Used where a
 * response body carries no id/version — a 200 without a usable ETag leaves
 * the client without a token for the next If-Match, so it is treated as a
 * failed request rather than proceeding with stale bookkeeping.
 */
export function readVersionFromEtag(response: Response): string {
    const etag = response.headers.get('ETag');
    const version = etag === null ? '' : etag.replaceAll('"', '').trim();
    if (version === '') {
        throw new PersistenceRequestError(response.status, null);
    }
    return version;
}
